import React, { useState, useEffect } from 'react';
import { X, Calendar, User, Briefcase, FileText } from 'lucide-react';
import { useNotification } from '../../../context/NotificationContext';
import CustomSelect from '../../../components/ui/CustomSelect';
import Input from '../../../components/ui/Input';
import { Button } from '../../../components/ui/button';

const PRIORITIES = {
  LOW: 'Basse',
  MEDIUM: 'Moyenne',
  HIGH: 'Haute',
  CRITICAL: 'Critique'
};

const STATUSES = {
  TODO: 'À faire',
  IN_PROGRESS: 'En cours',
  DONE: 'Terminé'
};

const TaskModal = ({ isOpen, onClose, task, onSave }) => {
  const { showToast } = useNotification();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'TODO',
    priority: 'MEDIUM',
    dueAt: '',
    assignedMembershipId: '',
    operationCaseId: '',
    partyId: '',
    leadId: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title || '',
        description: task.description || '',
        status: task.status || 'TODO',
        priority: task.priority || 'MEDIUM',
        dueAt: task.dueAt ? new Date(task.dueAt).toISOString().split('T')[0] : '',
        assignedMembershipId: task.assignedMembershipId || '',
        operationCaseId: task.operationCaseId || '',
        partyId: task.partyId || '',
        leadId: task.leadId || ''
      });
      if (task.operationCaseId || task.partyId || task.leadId) {
        setShowAdvanced(true);
      }
    } else {
      setFormData({
        title: '',
        description: '',
        status: 'TODO',
        priority: 'MEDIUM',
        dueAt: '',
        assignedMembershipId: '',
        operationCaseId: '',
        partyId: '',
        leadId: ''
      });
      setShowAdvanced(false);
    }
  }, [task, isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title) {
      showToast("Le titre est requis", "error");
      return;
    }
    
    setIsSubmitting(true);
    try {
      const payload = { ...formData };
      if (!payload.assignedMembershipId) delete payload.assignedMembershipId;
      if (!payload.operationCaseId) delete payload.operationCaseId;
      if (!payload.partyId) delete payload.partyId;
      if (!payload.leadId) delete payload.leadId;

      await onSave(payload, task?.id);
      onClose();
    } catch (error) {
      console.error(error);
      showToast("Erreur lors de la sauvegarde", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-surface rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh] border border-[var(--hp-color-border-muted)]">
        <div className="px-6 py-4 border-b border-[var(--hp-color-border-muted)] flex justify-between items-center bg-workspace/50 backdrop-blur-md">
          <h2 className="text-xl font-bold text-primary flex items-center gap-2">
            {task ? 'Modifier la tâche' : 'Nouvelle Tâche'}
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X size={18} className="text-subtle hover:text-primary" />
          </Button>
        </div>

        <div className="p-6 overflow-y-auto flex-1 custom-scrollbar">
          <form id="task-form" onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-primary mb-1">Titre de la tâche *</label>
                <Input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Ex: Contacter le client pour les documents"
                  className="w-full"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-1">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Détails de la tâche..."
                  className="workspace-input w-full min-h-[100px] resize-y"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-primary mb-1">Statut</label>
                  <CustomSelect
                    value={formData.status}
                    onChange={(val) => setFormData(prev => ({ ...prev, status: val }))}
                    options={Object.entries(STATUSES).map(([val, label]) => ({ value: val, label }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-1">Priorité</label>
                  <CustomSelect
                    value={formData.priority}
                    onChange={(val) => setFormData(prev => ({ ...prev, priority: val }))}
                    options={Object.entries(PRIORITIES).map(([val, label]) => ({ value: val, label }))}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-1 flex items-center gap-2">
                  <Calendar size={16} className="text-secondary" /> Échéance
                </label>
                <Input
                  type="date"
                  name="dueAt"
                  value={formData.dueAt}
                  onChange={handleChange}
                  className="w-full"
                />
              </div>
            </div>

            <div className="border-t border-[var(--hp-color-border-muted)] pt-4">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="text-xs text-subtle font-semibold"
              >
                <Briefcase size={14} className="mr-1.5" />
                {showAdvanced ? "Masquer les liaisons avancées" : "Afficher les liaisons (Dossier, Client, Lead)"}
              </Button>
            </div>

            {showAdvanced && (
              <div className="space-y-4 bg-surface-muted/50 p-4 rounded-xl border border-[var(--hp-color-border-muted)]">
                <h3 className="text-xs font-bold text-primary uppercase tracking-wider mb-2">
                  Rattachements optionnels
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-subtle mb-1 flex items-center gap-1.5">
                      <FileText size={12} /> Référence Dossier
                    </label>
                    <Input
                      type="text"
                      name="operationCaseId"
                      value={formData.operationCaseId}
                      onChange={handleChange}
                      placeholder="ID ou référence du dossier"
                      className="text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-subtle mb-1 flex items-center gap-1.5">
                      <User size={12} /> Contact / Client
                    </label>
                    <Input
                      type="text"
                      name="partyId"
                      value={formData.partyId}
                      onChange={handleChange}
                      placeholder="ID du client ou prospect"
                      className="text-sm"
                    />
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>

        <div className="px-6 py-4 border-t border-[var(--hp-color-border-muted)] bg-workspace/50 backdrop-blur-md flex justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            variant="primary"
            form="task-form"
            disabled={isSubmitting}
            className="min-w-[120px]"
          >
            {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TaskModal;
